import { useMemo, useState } from 'react'
import { Grid, Image } from 'semantic-ui-react'
import { DateTime } from 'luxon'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'
import { light, solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import { Card, Dropdown, GridRowColumn, Icon, Popup, Text } from '@/base'
import { useAnalyticsTrack } from '@/featuresV1/Amplitude'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectIncomeForMonth,
  selectMonthToDateIncomeChartData,
  selectPercDiffLastMonth,
} from '@/featuresV1/Dashboard/DashboardInsightsCards/monthToDateIncome.selectors'
import {
  centsToDollars,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { Colors } from '../../../styles/theme'

const CustomTooltip = ({
  active,
  payload,
  pastValue,
}: TooltipProps<number, string> & { pastValue: 'month' | 'year' }) => {
  if (!active || !payload) {
    return null
  }

  const today = DateTime.now()

  const current = payload.find((item) => item.dataKey === 'current')
  const past = payload.find((item) => item.dataKey === 'past')

  if (!current || !past) {
    return null
  }

  return (
    <div style={{ backgroundColor: Colors.white, padding: 10 }}>
      <Text as="bodySm" style={{ marginBottom: 5 }}>
        <b>
          {pastValue === 'month'
            ? `Day ${current.payload.dayOfMonth}`
            : `${today.toFormat(DATE_FORMATS_LUXON.MONTH)} ${current.payload.dayOfMonth}`}
        </b>
      </Text>
      <Text as="bodySm">
        {pastValue === 'month'
          ? today.toFormat(DATE_FORMATS_LUXON.MONTH)
          : today.toFormat('yyyy')}
        : {formatCurrencyFromCents(current.value, { hideDecimalsIfZero: true })}
      </Text>
      <Text as="bodySm">
        {pastValue === 'month'
          ? today.minus({ month: 1 }).toFormat(DATE_FORMATS_LUXON.MONTH)
          : today.minus({ year: 1 }).toFormat('yyyy')}
        : {formatCurrencyFromCents(past.value, { hideDecimalsIfZero: true })}
      </Text>
    </div>
  )
}

// Shortens numbers into using k and M
const formatter = Intl.NumberFormat('en', { notation: 'compact' })

const legendFormatter = (value: string) => (
  <Text style={{ display: 'inline-block' }}>{value}</Text>
)

const MonthToDateIncomeCard = () => {
  const [pastValue, setPastValue] = useState<'month' | 'year'>('month')
  const track = useAnalyticsTrack()

  const todayDate = useMemo(() => DateTime.now(), [])
  const pastDate = useMemo(
    () =>
      pastValue === 'month'
        ? todayDate.minus({ month: 1 })
        : todayDate.minus({ year: 1 }),
    [pastValue, todayDate]
  )

  const currentMonthIncome = useReselector(selectIncomeForMonth, todayDate)
  const pastMonthIncome = useReselector(selectIncomeForMonth, pastDate)
  const percDiffLastMonth = useReselector(
    selectPercDiffLastMonth,
    todayDate,
    pastDate
  )
  const chartData = useReselector(selectMonthToDateIncomeChartData, pastValue)

  const percentageToDisplay =
    percDiffLastMonth && Math.abs(percDiffLastMonth * 100).toFixed(0)

  return (
    <Card type="section">
      <Grid>
        <Grid.Column width={10}>
          <Text as="h2">Month to Date Income</Text>
          <Text as="bodySm" color="darkGray">
            {todayDate
              .startOf('month')
              .toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}{' '}
            - {todayDate.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}
          </Text>
        </Grid.Column>
        <Grid.Column
          width={6}
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            flexDirection: 'column',
          }}
        >
          <Dropdown
            variant="text"
            onChange={(value) => {
              if (value === 'year') {
                track('Selected "last year" period for month-to-date tracking')
              } else {
                track('Selected "last month" period for month-to-date tracking')
              }
              setPastValue(value)
            }}
            value={pastValue}
            options={[
              { value: 'month', text: 'This month vs last month' },
              { value: 'year', text: 'This month vs last year' },
            ]}
          />
        </Grid.Column>
        <Grid.Row>
          <Grid.Column width={8}>
            <Card type="subsection" backgroundColor="stone40">
              <Grid>
                <GridRowColumn>
                  <Text as="h3">This Month</Text>
                </GridRowColumn>
                <Grid.Row>
                  <Grid.Column width={10}>
                    <Text as="display2" style={{ marginBottom: 5 }}>
                      {currentMonthIncome
                        ? formatCurrencyFromCents(currentMonthIncome)
                        : '$-'}
                    </Text>
                    <Text as="eyebrow" color="darkGray">
                      {todayDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)}{' '}
                      Income to date
                      <Popup
                        content={
                          <>
                            <Text as="h3">Income to Date</Text>
                            <br />
                            <Text as="bodySm">
                              This is the total income that has been categorized
                              so far this month.
                              <br />
                              <br />
                              The percentage change shows how your current
                              monthly income compares to the same point in your
                              selected comparison period (Last Month, or This
                              Time Last Year).
                            </Text>
                          </>
                        }
                      />
                    </Text>
                  </Grid.Column>
                  <Grid.Column width={6} verticalAlign="bottom">
                    {percentageToDisplay ? (
                      <Text
                        as="display3"
                        style={{ marginBottom: 5 }}
                        color={percDiffLastMonth > 0 ? 'green' : 'red'}
                      >
                        <Icon
                          icon={
                            percDiffLastMonth < 0
                              ? light('arrow-down')
                              : light('arrow-up')
                          }
                          style={{ marginRight: 8 }}
                        />
                        {percentageToDisplay}%
                      </Text>
                    ) : (
                      <Text as="display3" style={{ marginBottom: 5 }}>
                        -
                      </Text>
                    )}

                    <Text as="eyebrow" color="darkGray">
                      vs same day last{' '}
                      {pastValue === 'month' ? 'month' : 'year'}
                    </Text>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card>
          </Grid.Column>
          <Grid.Column width={8}>
            <Card type="subsection" backgroundColor="stone40">
              <Grid>
                <GridRowColumn>
                  <Text as="h3">
                    {pastValue === 'month'
                      ? 'Last Month'
                      : 'This Time Last Year'}
                  </Text>
                </GridRowColumn>
                <GridRowColumn>
                  {pastMonthIncome ? (
                    <>
                      <Text as="display2" style={{ marginBottom: 5 }}>
                        {formatCurrencyFromCents(pastMonthIncome)}
                      </Text>
                      <Text as="eyebrow" color="darkGray">
                        {pastDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)}{' '}
                        total income
                        {pastValue === 'month' ? (
                          <Popup
                            content={
                              <>
                                <Text as="h3">Last Month Total Income</Text>
                                <br />
                                <Text as="bodySm">
                                  This is the total income you earned the
                                  previous month.
                                </Text>
                              </>
                            }
                          />
                        ) : (
                          <Popup
                            content={
                              <>
                                <Text as="h3">This Time Last Year</Text>
                                <br />
                                <Text as="bodySm">
                                  This is the total income you earned the same
                                  month the previous year.
                                </Text>
                              </>
                            }
                          />
                        )}
                      </Text>
                    </>
                  ) : (
                    <Card type="subsection" backgroundColor="stone40">
                      <Grid>
                        <Grid.Row>
                          <Grid.Column width={3}>
                            <Icon icon={solid('chart-simple')} size="2x" />
                          </Grid.Column>
                          <Grid.Column width={13}>
                            <Text as="bodySm">
                              We don’t have historical data yet, but check back
                              soon for updates.
                            </Text>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Card>
                  )}
                </GridRowColumn>
              </Grid>
            </Card>
          </Grid.Column>
        </Grid.Row>

        <GridRowColumn>
          <ResponsiveContainer width="99%" height={250}>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                  <stop stopColor={Colors.greenBlue} />
                  <stop
                    offset={1}
                    stopColor={Colors.greenBlue}
                    stopOpacity={0}
                    startOffset={1}
                  />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="dayOfMonth"
                tickFormatter={(value) =>
                  pastValue === 'month'
                    ? `Day ${value}`
                    : `${todayDate.toFormat(DATE_FORMATS_LUXON.MONTH)} ${value}`
                }
              />
              <YAxis
                domain={[0, 'auto']}
                type="number"
                allowDecimals={false}
                tickFormatter={(value) =>
                  `$${formatter.format(centsToDollars(value))}`
                }
              />
              <Tooltip<number, string>
                content={(props) => (
                  <CustomTooltip {...props} pastValue={pastValue} />
                )}
              />
              <Legend
                iconType="square"
                align="center"
                formatter={legendFormatter}
                payload={[
                  {
                    value: `${todayDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)} Income`,
                    type: 'square',
                    color: Colors.greenBlue,
                  },
                  {
                    value: `${todayDate.minus({ month: 1 }).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)} Total Income`,
                    type: 'plainline',
                    color: Colors.black,
                    payload: {
                      strokeDasharray: 0,
                    },
                  },
                ]}
              />
              <Area
                type="linear"
                dataKey="current"
                name="Current"
                stroke={Colors.greenBlue}
                strokeWidth={2}
                fill="url(#colorUv)"
              />
              <Area
                type="linear"
                dataKey="past"
                name="Past"
                strokeWidth={2}
                stroke={Colors.black}
                fillOpacity={0}
              />
              <CartesianGrid vertical={false} />
            </AreaChart>
          </ResponsiveContainer>
          {!currentMonthIncome && !pastMonthIncome && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 53,
                left: 74,
                backgroundColor: Colors.stone40OnWhiteNoAlpha,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                zIndex: 10000,
              }}
            >
              <Image
                src="https://heard-images.s3.amazonaws.com/assets/metrics.svg"
                style={{ width: 80, height: 80, marginBottom: 10 }}
              />
              <Text as="bodySm">
                This chart will update once your income has been categorized.
              </Text>
            </div>
          )}
        </GridRowColumn>
      </Grid>
    </Card>
  )
}

export default MonthToDateIncomeCard
